// AutomationRuleHeader.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './AutomationRuleHeader.css';

// Icons
import Icon from 'svg/Icon';

// Components
import Button from 'components/button/Button';

/**
 * AutomationRuleHeader
 *
 * Renders the header row for an automation rule, including collapse and remove controls.
 *
 * @param {string} ruleKey - Key for the current rule.
 * @param {string} title - Resolved title shown in the header.
 * @param {boolean} isCollapsed - Whether the rule body is currently collapsed.
 * @param {boolean} allowCollapse - Whether the rule can collapse in place.
 * @param {boolean} showRemoveButton - Whether to render the remove action.
 * @param {function} onToggleCollapse - Called when the header collapse affordance is clicked.
 * @param {function} onRemove - Called when the remove action is clicked.
 *
 * @returns {ReactNode} The rendered automation rule header.
 */
const AutomationRuleHeader = ({
    ruleKey,
    title,
    isCollapsed,
    allowCollapse,
    showRemoveButton,
    onToggleCollapse,
    onRemove,
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    const collapseIconKey = isCollapsed ? 'keyboard_arrow_right' : 'keyboard_arrow_down';
    // ----- RENDER ----- //

    return (
        <div className="automation-rule-header">

            {/* RULE HEADER MAIN */}
            <div
              className="automation-rule-header-main"
              onClick={allowCollapse ? onToggleCollapse : undefined}
            >

                {/* ICON BUTTON */}
                {allowCollapse && (
                    <button
                      className="automation-rule-header-icon-button"
                      style={{
                            backgroundColor: 'transparent',
                        }}
                      type="button"
                      title={isCollapsed ? 'Expand rule' : 'Collapse rule'}
                      aria-label={isCollapsed ? 'Expand rule' : 'Collapse rule'}
                    >
                        <Icon
                          iconKey={collapseIconKey}
                          width={18}
                          height={18}
                          color={theme.foregroundMuted}
                        />
                    </button>
                )}

                {/* RULE TEXT */}
                <div
                  className="automation-rule-header-title"
                >
                    {title}
                </div>

            </div>

            {/* RULE HEADER ACTIONS */}
            {showRemoveButton && (
                <div className="automation-rule-header-actions">
                    <Button
                      iconKey="delete"
                      iconSize="16"
                      foregroundColor={theme.foregroundMuted}
                      backgroundColor="transparent"
                      borderColor="transparent"
                      hoverBackgroundColor="transparent"
                      borderRadius="6px"
                      height="28px"
                      width="28px"
                      padding="0"
                      title="Remove rule"
                      onClick={() => onRemove(ruleKey)}
                    />
                </div>
            )}
        </div>
    );
};

// Dev Tools
AutomationRuleHeader.displayName = 'AutomationRuleHeader';

export default AutomationRuleHeader;
